/**
 * Step 2's draft screen — save-and-resume by construction: everything here
 * PATCHes the backend immediately rather than accumulating in local state
 * like `LeaseWizard.tsx` does, because a closed tab must not lose progress
 * (an applicant now has a real logged-in account — see ApplicantSignUp —
 * so "resume" is just signing back in and landing here again; there is no
 * separate resume-token mechanism).
 *
 * A DRAFT application is read-only the moment it stops being DRAFT
 * (`applications_applicant_update`'s USING clause in rls-policies.sql), so
 * this page redirects onward once status moves — there is nothing left
 * here to edit.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../lib/api.js';
import { uploadDocument, type UploadedDocument } from '../../lib/dataProvider.js';
import { FormErrors } from '../../components/Form.js';
import { formatBytes } from '../../components/PhotoUpload.js';
import { ApplyProgress } from './ApplyProgress.js';

interface ApplicationDto {
  id: string;
  status: string;
  unitId: string;
}

interface ApplicantSensitiveDto {
  ssnLast4: string | null;
  legalAgeAttested: boolean;
  authorizationGivenAt: string | null;
}

export function ApplicationDraft() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [sensitive, setSensitive] = useState<ApplicantSensitiveDto | null>(null);
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);

  const [ssn, setSsn] = useState('');
  const [legalAgeAttested, setLegalAgeAttested] = useState(false);
  const [authorizationGiven, setAuthorizationGiven] = useState(false);
  const [savingSensitive, setSavingSensitive] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const [app, sensitiveRow, docs] = await Promise.all([
          api<ApplicationDto>(`/applications/${id}`),
          api<ApplicantSensitiveDto>(`/applications/${id}/sensitive`),
          api<{ data: UploadedDocument[] }>(`/documents?applicationId=${id}`),
        ]);
        if (cancelled) return;
        setApplication(app);
        setSensitive(sensitiveRow);
        setLegalAgeAttested(sensitiveRow.legalAgeAttested);
        setAuthorizationGiven(sensitiveRow.authorizationGivenAt !== null);
        setDocuments(docs.data);

        if (app.status !== 'DRAFT') {
          void navigate(`/apply/${id}/submit`, { replace: true });
        }
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  async function saveSensitive() {
    if (!id) return;
    setSavingSensitive(true);
    setSaveError(null);
    setSaved(false);
    try {
      const body: Record<string, unknown> = { legalAgeAttested, authorizationGiven };
      if (ssn) body.ssn = ssn;

      const updated = await api<ApplicantSensitiveDto>(`/applications/${id}/sensitive`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setSensitive(updated);
      setSsn('');
      setSaved(true);
    } catch (caught) {
      setSaveError(caught);
    } finally {
      setSavingSensitive(false);
    }
  }

  async function handleFile(file: File) {
    if (!id) return;
    setUploadError(null);
    setUploading(true);
    try {
      const uploaded = await uploadDocument(file, {
        applicationId: id,
        documentType: 'OTHER',
        visibility: 'STAFF_ONLY',
      });
      setDocuments((prev) => [uploaded, ...prev]);
      if (inputRef.current) inputRef.current.value = '';
    } catch (caught) {
      setUploadError(caught instanceof Error ? caught.message : 'The upload failed.');
    } finally {
      setUploading(false);
    }
  }

  if (loadError) {
    return (
      <section>
        <div className="card">
          <FormErrors error={loadError} />
        </div>
      </section>
    );
  }

  if (!application || !sensitive) {
    return <p className="muted">Loading your application…</p>;
  }

  const readyForFee =
    legalAgeAttested && (authorizationGiven || sensitive.authorizationGivenAt !== null);

  return (
    <section>
      <ApplyProgress step={2} />

      <div className="apply-hero">
        <p className="apply__eyebrow">Your application</p>
        <h1>Tell us a bit about you.</h1>
        <p className="apply__lede">
          Everything here saves as you go — close this tab any time and come back to finish.
        </p>
      </div>

      <div className="card">
        <h2>Identity and authorization</h2>

        <label className="apply-check-row">
          <input
            type="checkbox"
            checked={legalAgeAttested}
            onChange={(event) => setLegalAgeAttested(event.target.checked)}
          />
          <span>I am 18 years of age or older.</span>
        </label>

        <label className="apply-check-row">
          <input
            type="checkbox"
            checked={authorizationGiven}
            disabled={sensitive.authorizationGivenAt !== null}
            onChange={(event) => setAuthorizationGiven(event.target.checked)}
          />
          <span>
            I authorize {sensitive.authorizationGivenAt !== null ? '(recorded)' : ''} a background
            and credit screening as part of this application.
          </span>
        </label>

        <label className="field" htmlFor="ssn">
          <span className="field__label">Social Security Number</span>
          <input
            id="ssn"
            name="ssn"
            className="field__input"
            type="text"
            inputMode="numeric"
            placeholder={
              sensitive.ssnLast4 ? `On file, ending in ${sensitive.ssnLast4}` : '123456789'
            }
            value={ssn}
            onChange={(event) => setSsn(event.target.value.replace(/\D/g, '').slice(0, 9))}
          />
          <span className="muted">
            Stored encrypted. {sensitive.ssnLast4 ? 'Leave blank to keep the one on file.' : ''}
          </span>
        </label>

        <FormErrors error={saveError} />
        {saved ? <p className="muted">Saved.</p> : null}

        <button
          className="button button--quiet"
          type="button"
          disabled={savingSensitive}
          onClick={() => void saveSensitive()}
        >
          {savingSensitive ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="card">
        <h2>Documents</h2>
        <p className="muted">Upload a photo ID or anything else the office asked for.</p>

        <input
          ref={inputRef}
          type="file"
          className="field__input"
          accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        {uploading ? <p className="muted">Uploading…</p> : null}
        {uploadError ? <p className="alert alert--error">{uploadError}</p> : null}

        {documents.length > 0 ? (
          <ul className="stack" style={{ gap: '0.3rem' }}>
            {documents.map((doc) => (
              <li key={doc.id}>
                {doc.fileName} · {formatBytes(doc.sizeBytes)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No documents uploaded yet.</p>
        )}
      </div>

      <div className="row" style={{ marginTop: '1.75rem' }}>
        <span className="shell__spacer" />
        <button
          className="button"
          type="button"
          disabled={!readyForFee}
          onClick={() => void navigate(`/apply/${id}/fee`)}
        >
          Continue to application fee
        </button>
      </div>
      {!readyForFee ? (
        <p className="muted">Confirm your age and authorize screening before continuing.</p>
      ) : null}
    </section>
  );
}
